import type { Dispatch, SetStateAction } from 'react';

import { externalLink as IconExternalLink } from '@/components/icons/externalLink';
import { customToast } from '@/components/Toast';
import { getQueryClient } from '@/context/ReactQueryProvider';
import type { TransactionHistoryItem } from '@/context/types';
import { QueryKeys } from '@/hooks/api/query-keys';
import { refreshSpentWalletInputs } from '@/hooks/refreshSpentWalletInputs';
import { translateOutsideReact } from '@/i18n/client-translator';
import { getClientSdkErrorMessage } from '@/lib/posthog/getClientSdkErrorMessage';
import { type UserAccountAction, VaultActionLabelKey } from '@/types/types';
import { matchSpentWalletInput } from '@/utils/error/matchers';
import { parseVaultError } from '@/utils/error/vault-error';
import { formatNumberWithCommas } from '@/utils/formatNumberWithCommas';

import type { StepNum, UnknownError } from './types';

export function invalidateQueriesOnDrawerOpen() {
  const queryClient = getQueryClient();
  queryClient.invalidateQueries({ queryKey: [QueryKeys.walletBalance] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.bitcoinPrice] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.walletCoinUtxos] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.walletAssets] });
  queryClient.invalidateQueries({ queryKey: [QueryKeys.snapWalletInventory] });
  // queryClient.invalidateQueries({ queryKey: [QueryKeys.feeRates] });
  // queryClient.invalidateQueries({
  //   queryKey: [QueryKeys.bitcoinPrice],
  // });
}

export const getNextButtonIcon = (currentStep: StepNum, hasError: boolean) => {
  if (currentStep === 2 && !hasError) {
    return <IconExternalLink className="size-5" />;
  }
  return undefined;
};

export { DEPRECATED_VAULT_SEARCH_TEXT, TRANSACTION_ERRORS } from '@/utils/error/vault-error';

interface GetIsNextDisabledParams {
  isButtonBlocked: boolean;
  currentStep: StepNum;
  // biome-ignore lint/suspicious/noExplicitAny: <TODO: fix any>
  wallet: any;
  unitAmount: number;
  unitError: boolean;
  hasError: boolean;
  isActive: boolean;
  walletSats: number | undefined;
  operationCost: number;
  isActionReady: boolean;
}

// common for Borrow and Repay drawers
export const getIsNextDisabled = ({
  isButtonBlocked,
  currentStep,
  wallet,
  unitAmount,
  unitError,
  hasError,
  isActive,
  walletSats,
  operationCost,
  isActionReady,
}: GetIsNextDisabledParams): boolean => {
  // Closing a completed action does not require another wallet inventory read.
  if (currentStep === 3) return false;

  if (!isActionReady) {
    return true;
  }

  if (currentStep === 1 && (wallet === null || !unitAmount || unitAmount === 0 || unitError)) {
    return true;
  }

  if (currentStep === 2 && !hasError) {
    return true;
  }

  if (isActive) {
    return true;
  }

  if (isButtonBlocked) {
    return true;
  }

  if (walletSats === undefined || walletSats < operationCost) {
    return true;
  }

  return false;
};

interface OnActionErrorOptions {
  onError: (error: string) => void;
  setHasError: (error: boolean) => void;
  setTransactionError: Dispatch<SetStateAction<string>>;
}

export const onActionError = (err: UnknownError, timeout: boolean, { onError, setHasError, setTransactionError }: OnActionErrorOptions) => {
  setHasError(true);

  const message = getClientSdkErrorMessage(err);
  const spentInput = matchSpentWalletInput(message) || (err instanceof Error && err.name === 'CreateVaultStaleInputsError');
  if (spentInput) void refreshSpentWalletInputs();
  const description = spentInput
    ? translateOutsideReact('common.errors.spentWalletInput')
    : timeout || /timed? out|timeout/i.test(message)
      ? translateOutsideReact('common.errors.requestTimedOut')
      : parseVaultError(message) || message;
  setTransactionError(description);
  onError(description);
};

// Surface funding failures in both the toast and drawer.
export const surfaceInsufficientFundsError = ({ onError, setHasError, setTransactionError }: OnActionErrorOptions) => {
  const description = translateOutsideReact('common.errors.insufficientFunds');
  setHasError(true);
  setTransactionError(description);
  onError(description);
};

export const actionSuccessToast = (actionId: string, submitDescription: string, txid: string, actionType: UserAccountAction) => {
  const actionTitle = getActionTitleByType(actionType);

  customToast.info(translateOutsideReact('common.toast.actionSubmitted', { action: actionTitle }), {
    id: actionId,
    description: submitDescription,
    metadata: { txid, actionType },
  });

  setTimeout(() => {
    customToast.progress(translateOutsideReact('common.toast.actionFinalizing', { action: actionTitle }), {
      id: actionId,
      description: submitDescription,
      metadata: { txid, actionType },
    });
  }, 1200);
};

export const getActionTitleByType = (actionType: UserAccountAction) => {
  return translateOutsideReact(`common.vaultActions.${VaultActionLabelKey[actionType]}`);
};

/** Title for the confirmed pending-tx notification (wrap is part 1 of UNIT → USDC). */
export function getSuccessToastTitle(tx: TransactionHistoryItem): string {
  if (tx.type === 'swap' && tx.amountUnit === 'UNIT') {
    return translateOutsideReact('common.toast.swapUnitToUsdc');
  }
  return translateOutsideReact('common.toast.actionConfirmed', { action: getActionTitleByType(tx.type) });
}

export const getSuccessToastDescription = (tx: TransactionHistoryItem) => {
  switch (tx.type) {
    case 'borrow':
      return translateOutsideReact('common.transactionSuccess.borrow', { amount: formatNumberWithCommas(tx.amount) });
    case 'repay':
      return translateOutsideReact('common.transactionSuccess.repay', { amount: formatNumberWithCommas(tx.amount) });
    case 'deposit':
      return translateOutsideReact('common.transactionSuccess.deposit', { amount: formatNumberWithCommas(tx.amount, 8, 0) });
    case 'withdraw':
      return translateOutsideReact('common.transactionSuccess.withdraw', { amount: formatNumberWithCommas(tx.amount, 8, 0) });
    case 'close':
      return translateOutsideReact('common.transactionSuccess.close');
    case 'swap':
      if (tx.amountUnit === 'UNIT') {
        const wrappedAmount = tx.amount2 ?? tx.amount;
        return translateOutsideReact('common.transactionSuccess.swapWrap', {
          amount: formatNumberWithCommas(tx.amount),
          received: formatNumberWithCommas(wrappedAmount),
        });
      }
      return translateOutsideReact('common.transactionSuccess.swapBtc', {
        amount: formatNumberWithCommas(tx.amount, 8, 0),
        received: formatNumberWithCommas(tx.amount2 || 0),
      });
    case 'repo':
    case 'trim':
      return translateOutsideReact(
        `liquidations.claim.page.claimConfirmed.${tx.liquidationSwap?.status === 'failed' ? 'swapFailed' : tx.liquidationSwap?.status === 'submitted' ? 'swapPending' : 'claimOnly'}`,
      );
    default:
      return '';
  }
};

export const surfaceCoinLoadError = (error: Error, { onError, setHasError, setTransactionError }: OnActionErrorOptions) => {
  const description = error.message || translateOutsideReact('common.errors.coinLoadFallback');
  setHasError(true);
  setTransactionError(description);
  onError(description);
};
